import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Input, Button, Modal, Spin, Alert } from 'antd';
import { useNavigate } from 'react-router-dom';
import type { UploadFile, RcFile } from 'antd/es/upload/interface';
import { useLoggedInUser } from '../../hooks/useLoggedInUser';
import { userFullName } from '../../lib/utils';
import { errorMessage } from '../../lib/utils/messages';
import { useLazySendUserFeedback } from '../../hooks/useLazySendUserFeedback';
import DragAndDrop from '../DragAndDrop';
import { getBase64Representation } from '../DragAndDrop/util';
import { useNotify } from '../../hooks/useNotify';
import { onApolloError } from '../../lib/graphql/helpers';
import { darkAccessibleLinkColour } from '../../theme';

const { TextArea } = Input;

/**
 * Hasura payload has 3MB payload limitation.
 * To make sure the total size of attachments and user feedback text is under this limitation.
 * Consider the text length and base64 format (will increase the image size a bit),
 * we set 2.2MB for the attachment maximum size.
 */
const attachmentsMaximumSize = 2_200_000;

/**
 * Feedback maximum length in characters.
 */
const feedbackMaximumLength = 3000;

export type Attachment = {
  readonly fileName: string;
  readonly contentType: string;
  readonly fileData: string;
};

/**
 * The text to display in the Modal on Send Success/Error
 *
 * @param sendFeedbackResponse Response from Feedback query
 */
const ModalText: React.FC<{
  readonly sendFeedbackResponse:
    | {
        readonly sendUserFeedback?:
          | {
              readonly status: string;
              readonly errorMessage?: string | undefined;
            }
          | undefined;
      }
    | undefined;
}> = ({ sendFeedbackResponse }) => {
  // This is being lazy with the types. We know that if ModalText is called,
  // then `sendFeedbackResponse` is set. This if statement is never true and is
  // dead code
  if (sendFeedbackResponse?.sendUserFeedback === undefined) {
    return <></>;
  }

  if (sendFeedbackResponse.sendUserFeedback.status === 'Success') {
    return <p>Feedback sent!</p>;
  }

  return (
    <>
      <p>
        Failed to send feedback:{' '}
        {sendFeedbackResponse.sendUserFeedback.errorMessage ?? ''} Please try
        again later.
      </p>
      {(sendFeedbackResponse.sendUserFeedback.errorMessage?.includes(
        'Email address is not verified.'
      ) ??
        false) &&
        !window.location.href.includes('pras.rfs') && (
          <p
            style={{
              fontSize: '0.85rem',
              backgroundColor: '#FFF2F0',
              padding: '0.5rem',
              border: '1px solid #FFCCC7',
            }}
          >
            Your email has not been verified in AWS SES. Note that verified
            emails are case sensitive.
          </p>
        )}
    </>
  );
};

/**
 * Sends the email and opens a Modal with the progress of the send
 */
const SendModal: React.FC<{
  readonly userName: string;
  readonly userEmail: string;
  readonly userFeedback: string;
  readonly userFeedbackReason: string;
  readonly attachments: readonly Attachment[];
  readonly setShowModal: (value: React.SetStateAction<boolean>) => void;
}> = ({
  userName,
  userEmail,
  userFeedback,
  userFeedbackReason,
  attachments,
  setShowModal,
}) => {
  const { notify } = useNotify();
  const navigate = useNavigate();

  const [
    sendEmail,
    { loading: sendingUserFeedback, data: sendFeedbackResponse },
  ] = useLazySendUserFeedback(
    userName,
    userEmail,
    userFeedback,
    userFeedbackReason,
    attachments,
    onApolloError(notify.error)
  );

  useEffect(() => {
    void sendEmail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onModalOkClick = () => {
    setShowModal(false);

    if (
      sendFeedbackResponse?.sendUserFeedback?.status.toLowerCase() === 'success'
    ) {
      navigate('/');
    }
  };

  return (
    <Modal
      destroyOnClose={true}
      open={true}
      title={'Feedback'}
      closable={false}
      cancelButtonProps={{ style: { display: 'none' } }}
      onOk={sendingUserFeedback ? undefined : onModalOkClick}
      footer={sendingUserFeedback ? null : undefined}
    >
      {sendingUserFeedback ? (
        <div style={{ display: 'flex' }}>
          <Spin /> &nbsp;&nbsp;
          <p>Sending Feedback...</p>
        </div>
      ) : (
        <ModalText sendFeedbackResponse={sendFeedbackResponse} />
      )}
    </Modal>
  );
};

/**
 * Displays feedback form.
 */
const Feedback = () => {
  const { loggedInUser } = useLoggedInUser();

  const [userFeedback, setUserFeedback] = useState('');
  const [userFeedbackReason, setUserFeedbackReason] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<readonly RcFile[]>([]);
  const [attachments, setAttachments] = useState<readonly Attachment[]>([]);
  const [attachmentsExceedMaximum, setAttachmentsExceedMaximum] =
    useState(false);

  const userName =
    loggedInUser !== undefined ? userFullName(loggedInUser) : 'unknown';

  const userEmail = loggedInUser?.email_address ?? 'unknown';

  const addFile = (files: RcFile | readonly RcFile[]) => {
    if (files instanceof Array) {
      setSelectedFiles([...selectedFiles, ...files]);
    } else {
      setSelectedFiles([...selectedFiles, files]);
    }
  };

  const removeFile = (file: UploadFile) => {
    const newSelection: readonly RcFile[] = selectedFiles.filter(
      (selectedFile) => selectedFile.uid !== file.uid
    );
    setSelectedFiles(newSelection);
  };

  useEffect(() => {
    const attachmentsTotalSize = selectedFiles.reduce(
      (acc, attachment) => acc + attachment.size,
      0
    );

    if (attachmentsTotalSize > attachmentsMaximumSize) {
      setAttachmentsExceedMaximum(true);
    } else {
      setAttachmentsExceedMaximum(false);
    }
  }, [selectedFiles]);

  const processAttachmentsAndSendFeedBack = async () => {
    const processAttachments = async () => {
      const newAttachments: readonly Attachment[] = (
        await Promise.all(
          selectedFiles.map(async (file) => {
            // eslint-disable-next-line functional/no-try-statements
            try {
              const fileData = await getBase64Representation(file);

              return {
                fileName: file.name,
                contentType: file.type,
                fileData,
              };
            } catch (e: unknown) {
              console.warn(
                `${file.name} could not be found in the local filesystem.`
              );
              return undefined;
            }
          })
        )
      ).filter(
        (attachment): attachment is Attachment => attachment !== undefined
      );

      setAttachments(newAttachments);
    };

    await processAttachments();
    setShowModal(true);
  };

  const onSubmitFeedback = () => {
    void processAttachmentsAndSendFeedBack();
  };

  if (loggedInUser === undefined) {
    return <></>;
  }

  return (
    <Row>
      <Col span="flex" style={{ margin: 'auto' }}>
        <Card title="Feedback" style={{ width: '100%' }}>
          <Alert
            message="Please use this form to tell us about features or enhancements you'd like to see in PRAS."
            description={
              <>
                For technical support - please call the ICT Service Desk on{' '}
                <a
                  href="tel:1800005123"
                  style={{ color: darkAccessibleLinkColour }}
                >
                  <u>1800&nbsp;00&nbsp;5123</u>
                </a>{' '}
                or email{' '}
                <a
                  href="mailto:servicedesk@rfs.nsw.gov.au"
                  style={{ color: darkAccessibleLinkColour }}
                >
                  <u>servicedesk@rfs.nsw.gov.au</u>
                </a>
                .
              </>
            }
            type="info"
            showIcon
            style={{ width: '400px' }}
          />
          <div style={{ marginTop: '10px' }}>
            <label htmlFor="user-name-input">Name:</label>
            <Input
              type="text"
              name="user-name-input"
              id="user-name-input"
              value={userName}
              readOnly={true}
              style={{ border: 'none', width: '300px' }}
            />
          </div>
          <div style={{ marginTop: '10px' }}>
            <label htmlFor="user-email-input">Email:</label>
            <Input
              type="text"
              name="user-email-input"
              id="user-email-input"
              value={userEmail}
              readOnly={true}
              style={{ border: 'none', width: '300px' }}
            />
          </div>
          <div style={{ width: '400px', marginTop: '10px' }}>
            <label htmlFor="user-feedback-input">
              Please describe the feature you&apos;d like to see in PRAS? Please
              provide as much detail as possible:
            </label>
            <br />
            <TextArea
              name="user-feedback-input"
              id="user-feedback-input"
              rows={4}
              value={userFeedback}
              onChange={(e) => setUserFeedback(e.target.value)}
              style={{ width: '400px', marginTop: '5px' }}
              maxLength={feedbackMaximumLength}
              showCount
            />
          </div>
          <div style={{ marginTop: '10px', marginBottom: '20px' }}>
            <label htmlFor="user-feedback-reason-input">
              Why is this feature important to you?
            </label>
            <br />
            <TextArea
              name="user-feedback-reason-input"
              id="user-feedback-reason-input"
              rows={4}
              value={userFeedbackReason}
              onChange={(e) => setUserFeedbackReason(e.target.value)}
              style={{ width: '400px', marginTop: '5px' }}
              maxLength={feedbackMaximumLength}
              showCount
            />
          </div>
          <div style={{ width: '400px', marginTop: '10px' }}>
            <label htmlFor="user-feedback-images">
              If you&apos;d like to show us an example of this feature request,
              or upload anything in support of your request - please do so
              below: (Maximum 2.2MB)
            </label>
            <DragAndDrop
              id="user-feedback-images"
              addFile={addFile}
              removeFile={removeFile}
            />
            {attachmentsExceedMaximum ? (
              <p style={{ color: 'red' }}>
                {errorMessage.feedbackAttachmentsExceedMaximum}
              </p>
            ) : null}
          </div>
          <Button
            id="send-feedback-bth"
            type="primary"
            disabled={attachmentsExceedMaximum}
            onClick={() => onSubmitFeedback()}
            style={{ float: 'right', marginTop: '20px' }}
          >
            Send
          </Button>
        </Card>
      </Col>
      {showModal && (
        <SendModal
          userName={userName}
          userEmail={userEmail}
          userFeedback={userFeedback}
          userFeedbackReason={userFeedbackReason}
          attachments={attachments}
          setShowModal={setShowModal}
        />
      )}
    </Row>
  );
};

export default Feedback;
